import { useEffect, useState } from 'react';
import { Download, Smartphone, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const INSTALL_PROMPT_KEY = 'linkit_install_prompt_v1';
const DISMISS_DAYS = 14;

const isStandaloneApp = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const isIosBrowser = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
};

const readDismissedUntil = () => {
  try {
    return Number(window.localStorage.getItem(INSTALL_PROMPT_KEY) || 0);
  } catch {
    return 0;
  }
};

const storeDismissedUntil = () => {
  try {
    window.localStorage.setItem(INSTALL_PROMPT_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
  } catch {
    // Private browsing can block storage; dismissal still works for this render.
  }
};

const InstallAppPrompt = () => {
  const location = useLocation();
  const [installEvent, setInstallEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [ios] = useState(() => isIosBrowser());
  const isDashboard = location.pathname.startsWith('/dashboard');
  const canShowRoute = isDashboard;
  const isEmbed = location.pathname.startsWith('/embed/');

  useEffect(() => {
    if (isStandaloneApp() || isEmbed) return undefined;

    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };

    const handleInstalled = () => {
      setVisible(false);
      setInstallEvent(null);
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [isEmbed]);

  useEffect(() => {
    if (isStandaloneApp() || isEmbed || dismissed) return undefined;
    if (!canShowRoute) return undefined;
    if (!installEvent && !ios) return undefined;
    if (readDismissedUntil() > Date.now()) return undefined;

    const timer = window.setTimeout(() => setVisible(true), 5000);
    return () => window.clearTimeout(timer);
  }, [canShowRoute, dismissed, installEvent, ios, isDashboard, isEmbed, location.pathname]);

  const dismiss = () => {
    storeDismissedUntil();
    setDismissed(true);
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice.catch(() => null);
    setInstallEvent(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="install-app-prompt fixed inset-x-3 bottom-4 z-[80] mx-auto max-w-md sm:inset-x-auto sm:right-5 sm:bottom-5">
      <div className="rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur-xl">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white">
            {ios ? <Smartphone size={19} /> : <Download size={19} />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-slate-950">Install Linkit as an app</p>
            <p className="mt-1 text-xs font-bold leading-relaxed text-slate-600">
              Open your dashboard faster from your home screen with an app like window
            </p>
            {ios && !installEvent && (
              <p className="mt-2 text-[10px] font-black uppercase leading-relaxed tracking-widest text-slate-400">
                On iPhone or iPad, tap Share, then Add to Home Screen.
              </p>
            )}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              {installEvent ? (
                <button
                  type="button"
                  onClick={install}
                  className="inline-flex flex-1 items-center justify-center rounded-2xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-slate-800"
                >
                  Install app
                </button>
              ) : (
                <button
                  type="button"
                  onClick={dismiss}
                  className="inline-flex flex-1 items-center justify-center rounded-2xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-slate-800"
                >
                  Got it
                </button>
              )}
              <button
                type="button"
                onClick={dismiss}
                className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600 transition-all hover:bg-slate-100"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Dismiss install prompt"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
